/**
 * Store provider — owns the AppData state, persists every change to
 * localStorage, and exposes a stable `useStore()` hook for components.
 *
 * Architecture:
 *   - `data` holds the entire AppData object.
 *   - `actions` are the only sanctioned mutations. They wrap setData with
 *     the right shape and id/timestamp generation.
 *   - The provider also wires the persisted theme into the document on
 *     mount and watches OS preference changes when the user is on
 *     'system'.
 *
 * Components that only read should use `useStore()`. Components that
 * need to write call `useActions()` (memoized, identity-stable, won't
 * cause re-renders).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { newId } from '../lib/id';
import { evaluateAchievements } from '../lib/achievements/evaluator';
import { suggestCategoryFromMerchant } from '../lib/merchantCategories';
import { recordMerchant } from '../lib/merchantMemory';
import {
  appendSnapshot,
  shouldSnapshotToday,
  snapshotNetWorth,
} from '../lib/netWorth';
import {
  deleteReceipt,
  deleteReceiptByTxId,
  pruneOrphanedReceipts,
} from '../lib/receiptStore';
import { processScheduledBills } from '../lib/scheduledOccurrences';
import { applyAccent, applyTheme, watchSystemTheme } from '../lib/theme';
import type { ThemePreference } from '../lib/theme';
import { buildDefaultAppData } from './defaults';
import { clearAppData, loadAppData, saveAppData } from './storage';
import type {
  Account,
  AppData,
  Category,
  Goal,
  NewAccountInput,
  NewCategoryInput,
  NewGoalInput,
  NewQuickPresetInput,
  NewScheduledItemInput,
  NewTransactionInput,
  Profile,
  QuickPreset,
  ScheduledItem,
  Transaction,
} from './types';

type Updater<T> = (prev: T) => T;

/** Store actions. Identity-stable across renders (memoized). */
export interface StoreActions {
  // Profile
  updateProfile: (patch: Partial<Profile>) => void;
  markOnboardingComplete: () => void;
  markWalkthroughComplete: () => void;

  // Theme + appearance
  setTheme: (pref: ThemePreference) => void;
  setAccentColor: (key: string) => void;

  // Transactions
  addTransaction: (input: NewTransactionInput) => Transaction;
  updateTransaction: (id: string, patch: Partial<Transaction>) => void;
  deleteTransaction: (id: string) => void;
  /** Bulk import from Plaid sync. Idempotent on plaidTransactionId. */
  importPlaidTransactions: (rows: NewTransactionInput[]) => number;

  // Categories
  addCategory: (input: NewCategoryInput) => Category;
  updateCategory: (id: string, patch: Partial<Category>) => void;
  deleteCategory: (id: string) => void;
  reorderCategories: (orderedIds: string[]) => void;

  // Scheduled items
  addScheduledItem: (input: NewScheduledItemInput) => ScheduledItem;
  updateScheduledItem: (id: string, patch: Partial<ScheduledItem>) => void;
  deleteScheduledItem: (id: string) => void;

  // Goals
  addGoal: (input: NewGoalInput) => Goal;
  updateGoal: (id: string, patch: Partial<Goal>) => void;
  deleteGoal: (id: string) => void;
  contributeToGoal: (id: string, amount: number) => void;

  // Achievements
  unlockAchievement: (id: string) => void;
  markToolUsed: (toolId: string) => void;

  // Rate prompt
  markRated: () => void;
  snoozeRate: () => void;
  neverRate: () => void;

  // Pro
  startProTrial: () => void;
  setProPurchased: (purchased: boolean) => void;

  // Achievements
  evaluateAchievementsAction: () => void;
  dismissAchievementUnlock: (id: string) => void;
  setAchievementsEnabled: (enabled: boolean) => void;

  // Manual accounts (visibility layer; not used by budget math)
  addAccount: (input: NewAccountInput) => Account;
  updateAccount: (id: string, patch: Partial<Account>) => void;
  deleteAccount: (id: string) => void;

  // Quick-add presets (Today-screen one-tap shortcuts; capped at 8)
  addQuickPreset: (input: NewQuickPresetInput) => QuickPreset;
  updateQuickPreset: (id: string, patch: Partial<QuickPreset>) => void;
  deleteQuickPreset: (id: string) => void;
  reorderQuickPresets: (orderedIds: string[]) => void;
  /** Build a transaction from a preset (date = today) and append it. */
  applyQuickPreset: (id: string) => Transaction | null;

  // Scheduled bill materialization
  /**
   * Walk active expense ScheduledItems and create transactions for any
   * due dates in the current period that don't already have one. Called
   * on Today-tab mount so a bill that came due while the app was closed
   * shows up as a real transaction the moment the user reopens it.
   */
  runScheduledBills: () => void;

  // Bulk
  resetAllData: () => void;
}

interface StoreContextValue {
  data: AppData;
  actions: StoreActions;
}

const StoreContext = createContext<StoreContextValue | null>(null);

export function StoreProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<AppData>(() => loadAppData());

  // Drop receipts whose transactions were removed (e.g. on a different
  // device, or by a delete that failed to propagate to idb). One-shot
  // background pass on cold start — never blocks the UI.
  useEffect(() => {
    const ids = new Set(data.transactions.map((t) => t.id));
    void pruneOrphanedReceipts(ids).catch(() => {});
    // Run only once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hold a ref to the latest evaluateAchievements action so the
  // debounced setTimeout in addTransaction can call it without
  // capturing a stale identity through useCallback deps.
  const evaluateAchievementsActionRef = useRef<(() => void) | null>(null);

  // Append today's net-worth snapshot once per cold-start (idempotent
  // for same-day reopens via shouldSnapshotToday). Cap at 365 entries.
  useEffect(() => {
    try {
      const todayIso = new Date().toISOString().slice(0, 10);
      setData((prev) => {
        const history = prev.profile.netWorthHistory ?? [];
        if (!shouldSnapshotToday(history, todayIso)) return prev;
        const snap = snapshotNetWorth(prev.accounts ?? [], todayIso);
        return {
          ...prev,
          profile: {
            ...prev.profile,
            netWorthHistory: appendSnapshot(history, snap),
          },
        };
      });
    } catch {
      /* never block app load */
    }
    // Snapshot once on mount; subsequent account edits flow through
    // the existing useEffect-on-data path naturally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist on every change. Use a ref to skip the very first write —
  // we just loaded this data, no need to immediately echo it back.
  const firstWriteSkipped = useRef(false);
  useEffect(() => {
    if (!firstWriteSkipped.current) {
      firstWriteSkipped.current = true;
      return;
    }
    saveAppData(data);
  }, [data]);

  // Apply the persisted theme + accent once on mount, and re-apply when
  // either changes. Order matters: applyTheme writes data-theme, applyAccent
  // reads the resolved theme to pick the light vs dark triplet.
  useEffect(() => {
    applyTheme(data.theme);
    applyAccent(data.accentColor, data.theme);
  }, [data.theme, data.accentColor]);

  // Keep the OS-tracking case (system mode) live for both theme + accent.
  const themeRef = useRef({ theme: data.theme, accentColor: data.accentColor });
  themeRef.current = { theme: data.theme, accentColor: data.accentColor };
  useEffect(() => watchSystemTheme(() => themeRef.current), []);

  // ---------- Action factories ----------
  const update = useCallback((u: Updater<AppData>) => setData(u), []);

  const updateProfile = useCallback(
    (patch: Partial<Profile>) =>
      update((d) => ({ ...d, profile: { ...d.profile, ...patch } })),
    [update],
  );

  const markOnboardingComplete = useCallback(
    () => updateProfile({ onboardingComplete: true }),
    [updateProfile],
  );

  const markWalkthroughComplete = useCallback(
    () => updateProfile({ walkthroughComplete: true }),
    [updateProfile],
  );

  const setTheme = useCallback(
    (theme: ThemePreference) => update((d) => ({ ...d, theme })),
    [update],
  );

  const setAccentColor = useCallback(
    (accentColor: string) => update((d) => ({ ...d, accentColor })),
    [update],
  );

  const addTransaction = useCallback(
    (input: NewTransactionInput): Transaction => {
      let tx!: Transaction;
      update((d) => {
        // If the caller didn't pick a category, try the keyword
        // categorizer against the merchant text. Wrapped — never block
        // the save on a memory/categorizer failure.
        let categoryId = input.categoryId;
        try {
          if (!categoryId && input.description) {
            const suggested = suggestCategoryFromMerchant(
              input.description,
              d.categories,
            );
            if (suggested) categoryId = suggested;
          }
        } catch {
          /* swallow */
        }
        tx = {
          ...input,
          categoryId,
          id: newId(),
          createdAt: new Date().toISOString(),
        };
        return { ...d, transactions: [...d.transactions, tx] };
      });
      try {
        recordMerchant(tx.description, tx.amount, tx.categoryId || null);
      } catch {
        /* swallow — merchant memory is best-effort */
      }
      // Debounced achievement check. canTriggerAchievements gates the
      // cooldown / engagement minimum, so this is a no-op during the
      // first 24 hours or before the user has 3 transactions.
      window.setTimeout(() => {
        try {
          evaluateAchievementsActionRef.current?.();
        } catch {
          /* swallow */
        }
      }, 500);
      return tx;
    },
    [update],
  );

  const importPlaidTransactions = useCallback(
    (rows: NewTransactionInput[]): number => {
      let inserted = 0;
      update((d) => {
        const existing = new Set(
          d.transactions
            .map((t) => t.plaidTransactionId)
            .filter((id): id is string => !!id),
        );
        const fresh: Transaction[] = [];
        for (const row of rows) {
          if (row.plaidTransactionId && existing.has(row.plaidTransactionId)) {
            continue;
          }
          fresh.push({
            ...row,
            id: newId(),
            createdAt: new Date().toISOString(),
          });
          if (row.plaidTransactionId) existing.add(row.plaidTransactionId);
        }
        inserted = fresh.length;
        if (inserted === 0) return d;
        return { ...d, transactions: [...d.transactions, ...fresh] };
      });
      return inserted;
    },
    [update],
  );

  const updateTransaction = useCallback(
    (id: string, patch: Partial<Transaction>) => {
      let priorReceiptId: string | undefined;
      let nextReceiptId: string | undefined;
      update((d) => {
        const prior = d.transactions.find((t) => t.id === id);
        priorReceiptId = prior?.receiptId;
        nextReceiptId = 'receiptId' in patch ? patch.receiptId : priorReceiptId;
        return {
          ...d,
          transactions: d.transactions.map((t) =>
            t.id === id ? { ...t, ...patch } : t,
          ),
        };
      });
      // If the receipt was cleared (or replaced), drop the old image
      // out of IndexedDB. Fire-and-forget — never block the UI on idb.
      if (priorReceiptId && priorReceiptId !== nextReceiptId) {
        void deleteReceipt(priorReceiptId).catch(() => {});
      }
    },
    [update],
  );

  const deleteTransaction = useCallback(
    (id: string) => {
      update((d) => ({
        ...d,
        transactions: d.transactions.filter((t) => t.id !== id),
      }));
      // Best-effort receipt cleanup. The orphan-prune pass on next
      // load is the safety net if this ever fails.
      void deleteReceiptByTxId(id).catch(() => {});
    },
    [update],
  );

  const addCategory = useCallback(
    (input: NewCategoryInput): Category => {
      let created!: Category;
      update((d) => {
        const maxOrder = d.categories.reduce(
          (m, c) => (c.order > m ? c.order : m),
          -1,
        );
        created = { ...input, id: newId(), order: maxOrder + 1 };
        return { ...d, categories: [...d.categories, created] };
      });
      return created;
    },
    [update],
  );

  const updateCategory = useCallback(
    (id: string, patch: Partial<Category>) =>
      update((d) => ({
        ...d,
        categories: d.categories.map((c) =>
          c.id === id ? { ...c, ...patch } : c,
        ),
      })),
    [update],
  );

  const deleteCategory = useCallback(
    (id: string) =>
      update((d) => ({
        ...d,
        categories: d.categories.filter((c) => c.id !== id),
      })),
    [update],
  );

  const reorderCategories = useCallback(
    (orderedIds: string[]) =>
      update((d) => {
        const indexFor = new Map(orderedIds.map((id, i) => [id, i]));
        return {
          ...d,
          categories: d.categories.map((c) => ({
            ...c,
            order: indexFor.get(c.id) ?? c.order,
          })),
        };
      }),
    [update],
  );

  const addScheduledItem = useCallback(
    (input: NewScheduledItemInput): ScheduledItem => {
      const item: ScheduledItem = { ...input, id: newId() };
      update((d) => ({ ...d, scheduledItems: [...d.scheduledItems, item] }));
      return item;
    },
    [update],
  );

  const updateScheduledItem = useCallback(
    (id: string, patch: Partial<ScheduledItem>) =>
      update((d) => ({
        ...d,
        scheduledItems: d.scheduledItems.map((i) =>
          i.id === id ? { ...i, ...patch } : i,
        ),
      })),
    [update],
  );

  const deleteScheduledItem = useCallback(
    (id: string) =>
      update((d) => ({
        ...d,
        scheduledItems: d.scheduledItems.filter((i) => i.id !== id),
      })),
    [update],
  );

  const addGoal = useCallback(
    (input: NewGoalInput): Goal => {
      const goal: Goal = {
        ...input,
        id: newId(),
        currentAmount: input.currentAmount ?? 0,
      };
      update((d) => ({ ...d, goals: [...d.goals, goal] }));
      return goal;
    },
    [update],
  );

  const updateGoal = useCallback(
    (id: string, patch: Partial<Goal>) =>
      update((d) => ({
        ...d,
        goals: d.goals.map((g) => (g.id === id ? { ...g, ...patch } : g)),
      })),
    [update],
  );

  const deleteGoal = useCallback(
    (id: string) =>
      update((d) => ({
        ...d,
        goals: d.goals.filter((g) => g.id !== id),
      })),
    [update],
  );

  const contributeToGoal = useCallback(
    (id: string, amount: number) =>
      update((d) => ({
        ...d,
        goals: d.goals.map((g) =>
          g.id === id
            ? {
                ...g,
                currentAmount: Math.max(0, g.currentAmount + amount),
              }
            : g,
        ),
      })),
    [update],
  );

  const unlockAchievement = useCallback(
    (id: string) =>
      update((d) => ({
        ...d,
        achievements: d.achievements.map((a) =>
          a.id === id && !a.unlockedAt
            ? { ...a, unlockedAt: new Date().toISOString() }
            : a,
        ),
      })),
    [update],
  );

  const markToolUsed = useCallback(
    (toolId: string) =>
      update((d) => {
        const used = d.toolsUsed ?? [];
        if (used.includes(toolId)) return d;
        return { ...d, toolsUsed: [...used, toolId] };
      }),
    [update],
  );

  const markRated = useCallback(
    () => update((d) => ({ ...d, rated: true })),
    [update],
  );

  const snoozeRate = useCallback(
    () => update((d) => ({ ...d, rateSnoozed: new Date().toISOString() })),
    [update],
  );

  const neverRate = useCallback(
    () => update((d) => ({ ...d, rateNever: true })),
    [update],
  );

  const startProTrial = useCallback(
    () =>
      update((d) =>
        d.proTrial
          ? d
          : { ...d, proTrial: { started: new Date().toISOString() } },
      ),
    [update],
  );

  const setProPurchased = useCallback(
    (proPurchased: boolean) => update((d) => ({ ...d, proPurchased })),
    [update],
  );

  const evaluateAchievementsAction = useCallback(() => {
    update((d) => {
      const a = d.profile.achievements;
      if (!a) return d;
      const newUnlocks = evaluateAchievements(d);
      if (newUnlocks.length === 0) return d;
      return {
        ...d,
        profile: {
          ...d.profile,
          achievements: {
            ...a,
            unlocks: [...a.unlocks, ...newUnlocks],
          },
        },
      };
    });
  }, [update]);

  const dismissAchievementUnlock = useCallback(
    (id: string) =>
      update((d) => {
        const a = d.profile.achievements;
        if (!a) return d;
        return {
          ...d,
          profile: {
            ...d.profile,
            achievements: {
              ...a,
              unlocks: a.unlocks.map((u) =>
                u.id === id ? { ...u, seen: true } : u,
              ),
            },
          },
        };
      }),
    [update],
  );

  const setAchievementsEnabled = useCallback(
    (enabled: boolean) =>
      update((d) => {
        const a = d.profile.achievements;
        if (!a) return d;
        return {
          ...d,
          profile: {
            ...d.profile,
            achievements: { ...a, achievementsEnabled: enabled },
          },
        };
      }),
    [update],
  );

  // Keep the ref pointing at the latest action and run the cold-start
  // evaluation once. canTriggerAchievements is the gate for cooldown /
  // engagement so this is safe to call regardless of state.
  useEffect(() => {
    evaluateAchievementsActionRef.current = evaluateAchievementsAction;
  }, [evaluateAchievementsAction]);
  useEffect(() => {
    const id = window.setTimeout(() => {
      try {
        evaluateAchievementsActionRef.current?.();
      } catch {
        /* swallow */
      }
    }, 800);
    return () => window.clearTimeout(id);
    // Mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addAccount = useCallback(
    (input: NewAccountInput): Account => {
      const account: Account = {
        ...input,
        id: newId(),
        updatedAt: new Date().toISOString(),
      };
      update((d) => ({
        ...d,
        accounts: [...(d.accounts ?? []), account],
      }));
      return account;
    },
    [update],
  );

  const updateAccount = useCallback(
    (id: string, patch: Partial<Account>) =>
      update((d) => ({
        ...d,
        accounts: (d.accounts ?? []).map((a) =>
          a.id === id
            ? { ...a, ...patch, updatedAt: new Date().toISOString() }
            : a,
        ),
      })),
    [update],
  );

  const deleteAccount = useCallback(
    (id: string) =>
      update((d) => ({
        ...d,
        accounts: (d.accounts ?? []).filter((a) => a.id !== id),
      })),
    [update],
  );

  const addQuickPreset = useCallback(
    (input: NewQuickPresetInput): QuickPreset => {
      let created!: QuickPreset;
      update((d) => {
        const list = d.profile.quickPresets ?? [];
        if (list.length >= 8) {
          throw new Error('Quick preset limit reached (8)');
        }
        const maxOrder = list.reduce(
          (m, p) => (p.sortOrder > m ? p.sortOrder : m),
          -1,
        );
        created = {
          ...input,
          id: newId(),
          createdAt: new Date().toISOString(),
          sortOrder: maxOrder + 1,
        };
        return {
          ...d,
          profile: { ...d.profile, quickPresets: [...list, created] },
        };
      });
      return created;
    },
    [update],
  );

  const updateQuickPreset = useCallback(
    (id: string, patch: Partial<QuickPreset>) =>
      update((d) => ({
        ...d,
        profile: {
          ...d.profile,
          quickPresets: (d.profile.quickPresets ?? []).map((p) =>
            p.id === id ? { ...p, ...patch } : p,
          ),
        },
      })),
    [update],
  );

  const deleteQuickPreset = useCallback(
    (id: string) =>
      update((d) => ({
        ...d,
        profile: {
          ...d.profile,
          quickPresets: (d.profile.quickPresets ?? []).filter(
            (p) => p.id !== id,
          ),
        },
      })),
    [update],
  );

  const reorderQuickPresets = useCallback(
    (orderedIds: string[]) =>
      update((d) => {
        const idx = new Map(orderedIds.map((id, i) => [id, i]));
        return {
          ...d,
          profile: {
            ...d.profile,
            quickPresets: (d.profile.quickPresets ?? []).map((p) => ({
              ...p,
              sortOrder: idx.get(p.id) ?? p.sortOrder,
            })),
          },
        };
      }),
    [update],
  );

  const applyQuickPreset = useCallback(
    (id: string): Transaction | null => {
      // Snapshot the preset off the latest state. Using the closure's
      // `data` would lag a tick if the preset was just edited.
      let preset: QuickPreset | undefined;
      update((d) => {
        preset = (d.profile.quickPresets ?? []).find((p) => p.id === id);
        return d; // no-op update; we only read here
      });
      if (!preset) return null;
      const todayIso = new Date().toISOString().slice(0, 10);
      return addTransaction({
        amount: preset.amount,
        type: preset.transactionType,
        categoryId: preset.categoryId,
        description: preset.label,
        date: todayIso,
        source: 'manual',
      });
    },
    [update, addTransaction],
  );

  const runScheduledBills = useCallback(
    () => update((d) => processScheduledBills(d)),
    [update],
  );

  const resetAllData = useCallback(() => {
    clearAppData();
    setData(buildDefaultAppData());
  }, []);

  const actions = useMemo<StoreActions>(
    () => ({
      updateProfile,
      markOnboardingComplete,
      markWalkthroughComplete,
      setTheme,
      setAccentColor,
      addTransaction,
      updateTransaction,
      deleteTransaction,
      importPlaidTransactions,
      addCategory,
      updateCategory,
      deleteCategory,
      reorderCategories,
      addScheduledItem,
      updateScheduledItem,
      deleteScheduledItem,
      addGoal,
      updateGoal,
      deleteGoal,
      contributeToGoal,
      unlockAchievement,
      markToolUsed,
      markRated,
      snoozeRate,
      neverRate,
      startProTrial,
      setProPurchased,
      evaluateAchievementsAction,
      dismissAchievementUnlock,
      setAchievementsEnabled,
      addAccount,
      updateAccount,
      deleteAccount,
      addQuickPreset,
      updateQuickPreset,
      deleteQuickPreset,
      reorderQuickPresets,
      applyQuickPreset,
      runScheduledBills,
      resetAllData,
    }),
    [
      updateProfile,
      markOnboardingComplete,
      markWalkthroughComplete,
      setTheme,
      setAccentColor,
      addTransaction,
      updateTransaction,
      deleteTransaction,
      importPlaidTransactions,
      addCategory,
      updateCategory,
      deleteCategory,
      reorderCategories,
      addScheduledItem,
      updateScheduledItem,
      deleteScheduledItem,
      addGoal,
      updateGoal,
      deleteGoal,
      contributeToGoal,
      unlockAchievement,
      markToolUsed,
      markRated,
      snoozeRate,
      neverRate,
      startProTrial,
      setProPurchased,
      evaluateAchievementsAction,
      dismissAchievementUnlock,
      setAchievementsEnabled,
      addAccount,
      updateAccount,
      deleteAccount,
      addQuickPreset,
      updateQuickPreset,
      deleteQuickPreset,
      reorderQuickPresets,
      applyQuickPreset,
      runScheduledBills,
      resetAllData,
    ],
  );

  const value = useMemo<StoreContextValue>(
    () => ({ data, actions }),
    [data, actions],
  );

  return (
    <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
  );
}

export function useStore(): StoreContextValue {
  const ctx = useContext(StoreContext);
  if (!ctx) {
    throw new Error('useStore must be used inside <StoreProvider>');
  }
  return ctx;
}

export function useAppData(): AppData {
  return useStore().data;
}

export function useActions(): StoreActions {
  return useStore().actions;
}
